import { Seo } from '@/components/seo/Seo';
import { Link } from 'react-router-dom';
import { ButtonLink, Card, CardBody, ListingGridSkeleton } from '@/components/ui';
import { ListingGrid } from '@/components/listings/ListingCard';
import { CategorySidebar } from '@/features/home/CategorySidebar';
import { CategoryStrip } from '@/features/home/CategoryStrip';
import { useCategories } from '@/api/categories';
import { useListingSearch } from '@/api/listings';
import { usePublicStats } from '@/api/stats';
import { useAuthStore } from '@/stores/auth-store';
import { formatNumber } from '@/lib/format';
import { t } from '@/i18n';

/** Ana sayfa — vitrin önce (docs/05 §3.1, müşteri revizyonu). */
export function HomePage() {
  const homeCategories = useCategories();

  return (
    <>
      <Seo
        title={t('home.metaTitle')}
        description={t('home.metaDescription')}
        canonical="/"
        og={{ 'og:type': 'website' }}
        jsonLd={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'Organization',
              name: t('app.company'),
              url: window.location.origin,
              description: t('app.tagline'),
            },
            {
              '@type': 'WebSite',
              name: t('app.name'),
              url: window.location.origin,
              // Arama kutusu: Google sonuçlarında site içi arama gösterebilir.
              potentialAction: {
                '@type': 'SearchAction',
                target: `${window.location.origin}/ilanlar?q={search_term_string}`,
                'query-input': 'required name=search_term_string',
              },
            },
          ],
        }}
      />

      {/*
        Sıra müşteri notuna göre: biyolink.com'dan gelen ziyaretçi giriş yapmadan
        DOĞRUDAN ilanları görsün. Kategoriler büyük ızgara olarak en üstteyken
        26 ürün ekranın yarısını kaplıyor, ilanlar ve alt bilgideki iletişim çok
        aşağıda kalıyordu; artık vitrinin altında tek satır şerit.
      */}
      <Hero />
      <Storefront />
      <CategoryStrip categories={homeCategories.data} isLoading={homeCategories.isPending} />
      <HowItWorks />
      <PlatformStats />
      <Faq />
    </>
  );
}

// ---------------------------------------------------------------------------
// 1. Hero + hızlı arama
// ---------------------------------------------------------------------------

function Hero() {
  // Düz beyaz zemin (müşteri notu 14.09.2026): yeşilden beyaza geçen kuşak ve
  // üstündeki yaprak dokusu kaldırıldı. Yeşil artık yalnız düğmelerde duruyor,
  // ağırlık vitrindeki ilanlarda — sayfanın asıl amacı da o.
  //
  // Hızlı arama satırı (kategori + il + arama) 21.09.2026'da KALDIRILDI: aynı
  // arama başlık çubuğunda, ayrıntılı süzgeçler ilan listesinde zaten var; bu
  // satır ilk ekranda ürünleri aşağı itiyordu.
  //
  // Yön kartları yalnız ZİYARETÇİYE (müşteri, 12.10.2026): girişli kullanıcı yolunu
  // zaten seçmiş. Oturum kontrolü bitene kadar da gizli — girişli kullanıcıda kartlar
  // bir an görünüp kaybolmasın, vitrin yukarı zıplamasın.
  const showLeads = useAuthStore((state) => !state.isLoading && state.user === null);

  return (
    <section className="border-b border-slate-200 bg-surface">
      <div className="container-page py-5 sm:py-6">
        <div className="max-w-3xl">
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            {t('home.heroTitle')}
          </h1>
          <p className="mt-2 text-[15px] text-slate-600">{t('home.heroSubtitle')}</p>
        </div>

        {/*
          İki yönlü mesaj (müşteri notu): ziyaretçi ya yakıt arıyor ya elinde mal var.
          Kartlar tek satır: başlık + cümle solda, tek düğme sağda. İkincil düğmeler
          ("bizimle iletişime geç") kaldırıldı; iletişim için sabit WhatsApp düğmesi var.
        */}
        {showLeads && (
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <LeadCard
              title={t('home.buyerLeadTitle')}
              body={t('home.buyerLeadBody')}
              primary={{ to: '/ilanlar', label: t('home.buyerLeadPrimary') }}
            />
            <LeadCard
              title={t('home.sellerLeadTitle')}
              body={t('home.sellerLeadBody')}
              primary={{ to: '/panel/ilan-ver', label: t('home.sellerLeadPrimary') }}
            />
          </div>
        )}
      </div>
    </section>
  );
}

/**
 * Hero içindeki yön kartı: bir cümle ve tek bir yol.
 *
 * Tek satır (müşteri, 21.09.2026): başlık + cümle solda, düğme sağda. Önceki
 * iki düğmeli düzen hem hangi yolun birincil olduğunu belirsizleştiriyor hem de
 * ilk ekranda yer kaplıyordu.
 */
function LeadCard({
  title,
  body,
  primary,
}: {
  title: string;
  body: string;
  primary: { to: string; label: string };
}) {
  return (
    <div className="flex flex-col gap-3 rounded-card border border-slate-200 bg-surface px-4 py-3.5 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <div className="min-w-0">
        <h2 className="text-base font-semibold text-slate-900">{title}</h2>
        <p className="mt-0.5 text-[13px] text-slate-600">{body}</p>
      </div>
      <ButtonLink to={primary.to} className="shrink-0">
        {primary.label}
      </ButtonLink>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 2. Vitrin — sol kategori menüsü + ilanlar
// ---------------------------------------------------------------------------

/** Vitrinde ilk ekranda kaç ilan gösterilir — dört sütunda üç tam satır. */
const SHOWCASE_LIMIT = 12;

/**
 * Ana sayfanın vitrini (müşteri notu).
 *
 * Giriş yapmamış ziyaretçi ilanları DOĞRUDAN görüyor; kategori daraltması solda.
 * Mesafe rozeti burada çıkmaz — o alıcının aktif tesisine göre hesaplanıyor ve
 * oturumsuz ziyaretçide zaten boş dönüyor.
 */
function Storefront() {
  const categories = useCategories();
  const listings = useListingSearch({ limit: SHOWCASE_LIMIT, sort: 'featured' });

  const items = listings.data?.items ?? [];

  return (
    <section className="bg-surface py-6">
      <div className="container-page">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <SectionHeading
            title={t('home.showcaseTitle')}
            subtitle={t('home.showcaseSubtitle')}
            className="mb-0"
          />
          <Link to="/ilanlar" className="text-sm font-semibold text-primary-900 hover:underline">
            {t('home.showcaseAll')} →
          </Link>
        </div>

        <div className="grid gap-6 lg:grid-cols-[16rem_1fr]">
          {/* Menü mobilde ilanların ÜSTÜNDE değil altında kalsın diye ikinci sırada. */}
          <aside className="order-2 lg:order-1">
            <CategorySidebar categories={categories.data} isLoading={categories.isPending} />
          </aside>

          <div className="order-1 min-w-0 lg:order-2">
            {listings.isPending ? (
              <ListingGridSkeleton count={8} />
            ) : items.length > 0 ? (
              <ListingGrid listings={items} />
            ) : (
              <Card>
                <CardBody className="py-10 text-center text-sm text-slate-500">
                  {t('listings.noResultTitle')}
                </CardBody>
              </Card>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 4. Nasıl çalışır
// ---------------------------------------------------------------------------

function HowItWorks() {
  const steps = [
    { icon: '📝', title: t('home.howStep1'), body: t('home.howStep1Body') },
    { icon: '📦', title: t('home.howStep2'), body: t('home.howStep2Body') },
    { icon: '🤝', title: t('home.howStep3'), body: t('home.howStep3Body') },
    { icon: '🔒', title: t('home.howStep4'), body: t('home.howStep4Body') },
  ];

  return (
    <section className="container-page py-14">
      <SectionHeading title={t('home.howTitle')} subtitle={t('home.howSubtitle')} />

      <ol className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {steps.map((step, index) => (
          <li key={step.title} className="relative rounded-card bg-surface p-5 shadow-sm">
            <span
              aria-hidden="true"
              className="absolute right-4 top-4 text-4xl font-bold text-primary-100"
            >
              {index + 1}
            </span>
            <span aria-hidden="true" className="text-2xl">
              {step.icon}
            </span>
            <h3 className="mt-3 text-base">{step.title}</h3>
            <p className="mt-1 text-sm text-slate-600">{step.body}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 5. Sayılarla platform
// ---------------------------------------------------------------------------

function PlatformStats() {
  const stats = usePublicStats();
  const etki = stats.data?.impact;

  // Müşterinin "CO2e Hesaplaması" dokümanı (07.10.2026): hesap sunucuda, brüt ve yaklaşık.
  const etkiler = [
    { label: t('home.impactDelivered'), value: etki?.deliveredTon },
    { label: t('home.impactCo2'), value: etki?.co2Ton },
    { label: t('home.impactPetcoke'), value: etki?.petcokeTon },
  ];

  const entries = [
    { label: t('home.statsCompanies'), value: stats.data?.companyCount },
    { label: t('home.statsListings'), value: stats.data?.activeListingCount },
    { label: t('home.statsOrders'), value: stats.data?.completedOrderCount },
  ];

  return (
    <section className="bg-primary-900 py-12 text-white">
      <div className="container-page">
        {/* Renk açıkça yazılmalı: temel `h1..h4` kuralı koyu metin veriyor ve
            üst öğedeki `text-white` mirasını eziyor. */}
        <h2 className="text-center text-xl font-semibold text-white">{t('home.statsTitle')}</h2>
        <dl className="mt-8 grid gap-6 text-center sm:grid-cols-3">
          {etkiler.map((entry) => (
            <div key={entry.label}>
              <dd className="text-4xl font-bold">
                {entry.value === undefined ? '—' : formatNumber(entry.value)}
              </dd>
              <dt className="mt-1 text-sm text-primary-100">{entry.label}</dt>
            </div>
          ))}
        </dl>
        <p className="mx-auto mt-4 max-w-2xl text-center text-xs text-primary-100">
          {t('home.impactNote')}
        </p>
        <dl className="mt-8 grid gap-6 border-t border-white/15 pt-8 text-center sm:grid-cols-3">
          {entries.map((entry) => (
            <div key={entry.label}>
              <dd className="text-3xl font-bold">
                {entry.value === undefined ? '—' : formatNumber(entry.value)}
              </dd>
              <dt className="mt-1 text-sm text-primary-100">{entry.label}</dt>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 6. Çift CTA + SSS
// ---------------------------------------------------------------------------

function Faq() {
  const items = [
    { q: t('home.faq1Q'), a: t('home.faq1A') },
    { q: t('home.faq2Q'), a: t('home.faq2A') },
    { q: t('home.faq3Q'), a: t('home.faq3A') },
    { q: t('home.faq4Q'), a: t('home.faq4A') },
  ];

  return (
    <section className="container-page pb-16">
      <SectionHeading title={t('home.faqTitle')} />

      <div className="mx-auto max-w-3xl space-y-2">
        {items.map((item) => (
          <details
            key={item.q}
            className="group rounded-card border border-slate-200 bg-surface px-4 py-3"
          >
            <summary className="cursor-pointer list-none text-sm font-medium text-slate-900 marker:hidden">
              <span className="flex items-center justify-between gap-3">
                {item.q}
                <span aria-hidden="true" className="text-slate-400 group-open:rotate-180">
                  ⌄
                </span>
              </span>
            </summary>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{item.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

function SectionHeading({
  title,
  subtitle,
  className,
}: {
  title: string;
  subtitle?: string;
  className?: string;
}) {
  return (
    <div className={className ?? 'mb-8'}>
      <h2 className="text-2xl">{title}</h2>
      {subtitle && <p className="mt-1 text-sm text-slate-600">{subtitle}</p>}
    </div>
  );
}
